/*
 * Copyright Mimic Networks, Inc. 2025.
 */

/**
 * Compares two semantic version strings (e.g., "v1.2.3" or "1.2.3").
 * Correctly handles comparisons like v1.10.0 vs v1.5.8 that would
 * be incorrectly ordered with simple string comparison.
 *
 * @param versionA First version string
 * @param versionB Second version string
 * @returns
 * - Positive number if versionA is greater than versionB
 * - Negative number if versionA is less than versionB
 * - 0 if versions are equal
 */
export function semverCompare(versionA: string, versionB: string): number {
  // Remove 'v' prefix if present
  const cleanA = versionA.replace(/^v/, '');
  const cleanB = versionB.replace(/^v/, '');

  // Split into version parts
  const partsA = cleanA.split('.').map((part) => parseInt(part, 10));
  const partsB = cleanB.split('.').map((part) => parseInt(part, 10));

  // Compare each part, starting from major version
  const maxLength = Math.max(partsA.length, partsB.length);
  for (let i = 0; i < maxLength; i += 1) {
    // Missing parts are treated as 0
    const partA = i < partsA.length ? partsA[i] : 0;
    const partB = i < partsB.length ? partsB[i] : 0;

    if (partA !== partB) {
      return partA - partB;
    }
  }

  return 0; // Versions are identical
}
